import { memo, useRef, useMemo, useState, useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  notification,
  rejectionError,
  transformCrudFormErrors,
  ValidationError,
  SelectsOptions,
  getSelectsOptions,
} from '@ohpen/enso-ui';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import {
  ExchangeRateCrudRequest,
  FieldType,
  SupportedCurrency,
  TargetCurrency,
  ExchangeRatesCreateChangeRequestResponse,
  ChangeRequestOfExchangeRateCrudRequest,
  exchangeRatesCreateChangeRequest,
} from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';

import { getExchangeRatesSchema } from '../formConfig';
import { CrudFormProps } from '@pmp/types';

export const ExchangeRatesCrud = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<ExchangeRateCrudRequest>): ReactElement => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const onConfirm: (formData: ExchangeRateCrudRequest) => Promise<ValidationError[] | void> = useCallback(
      values => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));
        const body: ChangeRequestOfExchangeRateCrudRequest = {
          data: values,
          actionType: crudToActionMap[mode],
        };
        return exchangeRatesCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: ExchangeRatesCreateChangeRequestResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              const allErrors = transformCrudFormErrors({ ...errors });
              return allErrors;
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [t, mode, refreshTable, enableFourEyes],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.exchangeRates.labels.exchangeRates') }),
        [CrudMode.CREATE]: t('enso.labels.createX', { x: t('pmp.exchangeRates.labels.exchangeRates') }),
        [CrudMode.EDIT]: t('enso.labels.createX', { x: t('pmp.exchangeRates.labels.exchangeRates') }),
      };
      return titles[mode];
    }, [mode, t]);

    const crudProps: CrudProps<ExchangeRateCrudRequest> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      const avaliableSelectsOptions: SelectsOptions = {
        ...selectsOptions,
        [FieldType.SOURCE_CURRENCY]: selectsOptions[FieldType.SOURCE_CURRENCY].map(option =>
          option.value === SupportedCurrency.GBP ? { ...option, disabled: true } : option,
        ),
      };

      return {
        ref,
        open,
        schema: getExchangeRatesSchema(avaliableSelectsOptions, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('enso.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues: { targetCurrency: TargetCurrency.GBP, ...record },
        mode,
        readonly: mode === CrudMode.VIEW,
        width: 'm',
        onClose: handleOnClose,
        onConfirm,
      };
    }, [enableFourEyes, handleOnClose, mode, onConfirm, open, record, title, t, valueLists]);

    return <Crud {...crudProps} />;
  },
);
